import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import FlowingMenu from "../blocks/FlowingMenu/FlowingMenu";
import ScrollFloat from "../blocks/ScrollFloat/ScrollFloat";
import { Language, detectBrowserLanguage, getTranslations } from "../utils/localization";
import "./ProjectsSection.css";

const ProjectsSection: React.FC = () => {
  const [language, setLanguage] = useState<Language>(detectBrowserLanguage());
  const translations = getTranslations(language);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const gameDevContainerRef = useRef<HTMLDivElement>(null);
  const webDevContainerRef = useRef<HTMLDivElement>(null);

  // Update language if it changes in App.tsx
  useEffect(() => {
    const handleLanguageChange = () => {
      // This is a simple way to detect language changes from other components
      const htmlLang = document.documentElement.lang;
      if (htmlLang === 'zh-CN' && language !== 'zh') {
        setLanguage('zh');
      } else if (htmlLang === 'en' && language !== 'en') {
        setLanguage('en');
      }
    };

    window.addEventListener('languagechange', handleLanguageChange);
    return () => {
      window.removeEventListener('languagechange', handleLanguageChange);
    };
  }, [language]);

  // Subscribe to custom event for item expansion
  useEffect(() => {
    const handleItemExpand = (e: CustomEvent) => {
      setExpandedItem(e.detail.itemId);
      
      // Adjust container height based on expanded item
      setTimeout(() => {
        if (gameDevContainerRef.current && webDevContainerRef.current) {
          const gameDevHeight = gameDevProjects.length * 180; // All items expanded by default
          const webDevHeight = webDevProjects.length * 180; // All items expanded by default
          
          gameDevContainerRef.current.style.height = `${gameDevHeight}px`;
          webDevContainerRef.current.style.height = `${webDevHeight}px`;
        }
      }, 50);
    };

    const handleItemCollapse = () => {
      setExpandedItem(null);
    };

    // @ts-ignore - CustomEvent with detail
    window.addEventListener('menuItemExpand', handleItemExpand);
    window.addEventListener('menuItemCollapse', handleItemCollapse);
    
    return () => {
      // @ts-ignore - CustomEvent with detail
      window.removeEventListener('menuItemExpand', handleItemExpand);
      window.removeEventListener('menuItemCollapse', handleItemCollapse);
    };
  }, []);

  const gameDevProjects = [
    { 
      link: "/scrollden", 
      text: language === 'zh' ? "卷中坞" : "SCROLLDEN", 
      image: "/images/projects/scrollden/showCase1.png",
      description: language === 'zh' 
        ? "以神话为灵感、专注于建造与探索的休闲游戏。"
        : "A leisure game focuses on base building and is inspired by Chinese mythology."
    },
    { 
      link: "/gismonda", 
      text: language === 'zh' ? "吉斯蒙达" : "GISMONDA", 
      image: "/images/projects/gismonda/showCase1.png",
      description: language === 'zh'
        ? "一款横版卷轴动作游戏，灵感来源于1894年的希腊戏剧。玩家扮演一位复仇女王，搭配不同技能塑造个人战斗风格。"
        : "A side-scrolling action game inspired by a Greek melodrama from 1894. Play as a warrior queen seeking revenge with customizable combat abilities."
    },
    { 
      link: "/cam-down", 
      text: language === 'zh' ? "禁区实况" : "CAM DOWN", 
      image: "/images/projects/camdown/showCase1.jpg",
      description: language === 'zh'
        ? "一款多人恐怖游戏，灵感来源于 Content Warning 和 Lethal Company。玩家扮演星际探索者，通过直播他们的冒险旅程来赚取收入。"
        : translations.camDown.subtitle
    },
    { 
      link: "/lament-of-swan", 
      text: language === 'zh' ? "天鹅挽歌" : "LAMENT OF SWAN", 
      image: "/images/projects/swan/showCase01.png",
      description: language === 'zh'
        ? "一款以故事驱动的2D横版剧情解谜惊悚游戏，融合了心理恐怖元素和强烈戏剧张力。你不是在舞台上起舞，而是在绝望的深渊中挣扎。"
        : translations.lamentOfSwan.subtitle
    },
    { 
      link: "/mystica-replica", 
      text: language === 'zh' ? "重复之谜" : "MYSTICA REPLICA", 
      image: "/images/projects/mystica/showCase3.png",
      description: language === 'zh'
        ? translations.mysticaReplica.subtitle
        : translations.mysticaReplica.subtitle
    },
    { 
      link: "/amir", 
      text: language === 'zh' ? "阿米尔" : "AMIR", 
      image: "/images/projects/amir/showCase1.png",
      description: language === 'zh'
        ? translations.amir.subtitle
        : translations.amir.subtitle
    },
    { 
      link: "/casserole", 
      text: language === 'zh' ? "梦想炖菜" : "CASSEROLE", 
      image: "/images/projects/casserole/showCase1.png",
      description: language === 'zh'
        ? translations.casserole.subtitle
        : translations.casserole.subtitle
    },
    { 
      link: "/design-docs", 
      text: language === 'zh' ? "游戏设计文档" : "DESIGN DOCS", 
      image: "/images/projects/designdoc/cover.png",
      description: language === 'zh'
        ? translations.designDocs.subtitle
        : translations.designDocs.subtitle
    },
  ];

  const webDevProjects = [
    { 
      link: "/seemuseums", 
      text: language === 'zh' ? "SeeMuseums" : "SEEMUSEUMS", 
      image: "/images/projects/seem/showCase1.png",
      description: language === 'zh'
        ? "创新的艺术教育平台，利用AI技术将博物馆藏品转化为互动式虚拟展览，为用户提供个性化的艺术学习体验。"
        : "Pioneering the transformation of art education by creating the 'Duolingo for playful art learning' through AI-powered interactive exhibitions."
    },
    { 
      link: "/sage", 
      text: language === 'zh' ? "贤者" : "SAGE", 
      image: "/images/projects/sage/showCase1.png",
      description: language === 'zh'
        ? "一款理念性的学习应用，设计用于促进持续学习而非强制性目标驱动。应用每天提供知识片段，让用户在碎片时间里学习。"
        : "A conceptual learning application designed to promote continuous learning without forced goal-driven pressure."
    },
    { 
      link: "/cautions", 
      text: language === 'zh' ? "专注" : "CAUTIONS", 
      image: "/images/projects/cautions/showCase1.png",
      description: language === 'zh'
        ? "一个容器化应用程序，设计用于监控和分析用户在线课程期间的专注度。"
        : "A containerized application designed to monitor and analyze user focus during online courses."
    },
    { 
      link: "/webdev", 
      text: language === 'zh' ? "网页设计" : "WEB DESIGN", 
      image: "/images/projects/dream/showCase1.png",
      description: language === 'zh'
        ? "网页设计作品集包括响应式设计、用户界面/用户体验设计和前端开发项目。"
        : "Web design portfolio including responsive design, UI/UX design, and front-end development projects."
    },
    { 
      link: "/dream", 
      text: language === 'zh' ? "纽约大学创意实验室" : "DREAM", 
      image: "/images/projects/dream/showCase1.png",
      description: language === 'zh'
        ? "基于网页的研究平台，专为认知、发展与教育心理学实验而开发。DREAM 为研究人员提供了认知测评、电子游戏、模拟实验和调查问卷的集合，旨在帮助高效地收集数据。"
        : "A Web-based research platform application used for development, cognitive, and educational psychology research experiments."
    },
    { 
      link: "/other", 
      text: language === 'zh' ? "其他项目" : "OTHERS", 
      image: "/images/projects/mystica/showCase2.png",
      description: language === 'zh'
        ? "各种实验性作品、小型项目和合作项目的集合。"
        : "A collection of experimental works, small projects, and collaborations."
    }
  ];

  // Calculate initial menu heights with all items expanded
  const gameDevMenuHeight = gameDevProjects.length * 180;
  const webDevMenuHeight = webDevProjects.length * 180;

  return (
    <section className="projects" id="projects">
      <div className="projects-container">
        <ScrollFloat
          animationDuration={1}
          ease="back.inOut(2)"
          scrollStart="center bottom+=50%"
          scrollEnd="bottom bottom-=40%"
          stagger={0.03}
          textClassName="projects-title"
        >
          {translations.projects.title}
        </ScrollFloat>

        <div className="menu-project-section">
          <div className="category-divider">
            <div className="warning-tape">
              <span className="divider-text">
                {language === 'zh' ? "游戏开发" : "Game Development"}
              </span>
            </div>
          </div>
          <div ref={gameDevContainerRef}>
            <FlowingMenu items={gameDevProjects.map((item, index) => ({
              ...item,
              id: `gamedev-${index}`
            }))} />
          </div>
        </div>

        <div className="menu-project-section">
          <div className="category-divider">
            <div className="warning-tape">
              <span className="divider-text">
                {language === 'zh' ? "网页设计" : "Website Development"}
              </span>
            </div>
          </div>
          <div ref={webDevContainerRef}>
            <FlowingMenu items={webDevProjects.map((item, index) => ({
              ...item,
              id: `webdev-${index}`
            }))} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection; 